import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSwipeable } from 'react-swipeable';
import {
  ChevronLeft,
  ChevronRight,
  CategoryOutlined,
  KeyboardArrowDown,
  Search as SearchIcon,
  FolderOutlined,
  Close,
  FilterListOutlined,
  Business,
  FilterAlt,
} from '@mui/icons-material';
import {
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { Category } from '../models/Category';
import { Parameter } from '../models/Parameter';
import Filters from './Filters';
import Producers from './Producers';
import { useConfig } from '../utils/useConfig';

export type ProducerForSearch = {
  id: number;
  name: string;
  logo?: string;
  productCount?: number;
};

type Props = {
  currentCategory: Category | null;
  categories: Category[];
  parameters: Parameter[];
  searchParams: URLSearchParams;
  setSearchParams: (params: URLSearchParams) => void;
  filters: string | null;
  priceRange: number[];
  currentPrice: number[];
  setCurrentPrice: (range: number[]) => void;
  setIsSidePanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isSidePanelOpen: boolean;
  producers?: ProducerForSearch[];
  selectedProducers: number[];
  onProducerChange: (producerId: number, isSelected: boolean) => void;
  onClearAllProducers?: () => void;

  // Nowe propsy dla filtrów
  selectedFilters: { [key: string]: string[] };
  onFilterChange: (
    parameterId: string,
    value: string,
    isSelected: boolean,
  ) => void;
  onClearAllFilters: () => void;
  onApplyFilters: (filters: { [key: string]: string[] }) => void;
  onPriceRangeChange: (range: number[]) => void;
  minPrice: number;
  maxPrice: number;
  totalPriceRange: number[];
};

function SearchSidePanel({
  currentCategory,
  categories,
  parameters,
  searchParams,
  setSearchParams,
  filters,
  priceRange,
  currentPrice,
  setCurrentPrice,
  setIsSidePanelOpen,
  isSidePanelOpen,
  producers = [],
  selectedProducers = [], // Przyjmujemy jako props, a nie tworzymy lokalny stan
  onProducerChange, // Używamy handlera przekazanego przez props
  onClearAllProducers, // Nowy prop

  // Nowe propsy dla filtrów
  selectedFilters,
  onFilterChange,
  onClearAllFilters,
  onApplyFilters,
  onPriceRangeChange,
  minPrice,
  maxPrice,
  totalPriceRange,
}: Props) {
  const theme = useTheme();
  const config = useConfig();
  const { colors } = config.theme;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Create utility color variables for consistent usage
  const primaryColor = colors.primary;
  const primaryLight = `rgba(${parseInt(primaryColor.slice(1, 3), 16)}, ${parseInt(primaryColor.slice(3, 5), 16)}, ${parseInt(primaryColor.slice(5, 7), 16)}, 0.1)`;
  const primaryLightBorder = `rgba(${parseInt(primaryColor.slice(1, 3), 16)}, ${parseInt(primaryColor.slice(3, 5), 16)}, ${parseInt(primaryColor.slice(5, 7), 16)}, 0.2)`;

  // Zakładki dla widoku mobilnego
  const [activeTab, setActiveTab] = useState<
    'categories' | 'producers' | 'filters'
  >('categories');
  const [openCategorySection, setOpenCategorySection] = useState(true);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [openFiltersSection, setOpenFiltersSection] = useState<boolean>(true);

  // Swipe functionality state
  const [swipePosition, setSwipePosition] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const drawerPaperRef = useRef<HTMLElement | null>(null);

  // Reset swipe position when drawer opens
  useEffect(() => {
    if (isSidePanelOpen) {
      setSwipePosition(0);
      setIsClosing(false);

      // Reset transformations when panel opens
      if (drawerPaperRef.current) {
        drawerPaperRef.current.style.transform = '';
        drawerPaperRef.current.style.transition = '';
      }
    }
  }, [isSidePanelOpen]);

  // Swipe threshold for closing
  const swipeThreshold = 100;

  // Swipe handlers
  const handlers = useSwipeable({
    onSwiping: (event) => {
      if (!isMobile || event.dir !== 'Down') return;

      // Set drag position (only downwards)
      const newPosition = Math.max(0, event.deltaY);
      setSwipePosition(newPosition);

      // Apply transformation to the panel
      if (drawerPaperRef.current) {
        drawerPaperRef.current.style.transform = `translateY(${newPosition}px)`;
        drawerPaperRef.current.style.transition = 'none'; // Disable transition during dragging
      }
    },
    onSwipedDown: (event) => {
      if (!isMobile) return;

      if (event.deltaY > swipeThreshold) {
        // If swipe distance exceeds threshold, close the panel
        setIsClosing(true);

        // Smooth closing animation
        if (drawerPaperRef.current) {
          drawerPaperRef.current.style.transition = 'transform 0.3s ease-out';
          drawerPaperRef.current.style.transform = 'translateY(100%)';
        }

        // Close panel after animation completes
        setTimeout(() => {
          setIsSidePanelOpen(false);
        }, 300);
      } else {
        // If swipe doesn't exceed threshold, revert to initial position
        if (drawerPaperRef.current) {
          drawerPaperRef.current.style.transition = 'transform 0.3s ease-out';
          drawerPaperRef.current.style.transform = 'translateY(0)';
        }
        setSwipePosition(0);
      }
    },
    onSwiped: () => {
      // Reset transitions when touch ends
      if (
        drawerPaperRef.current &&
        swipePosition < swipeThreshold &&
        !isClosing
      ) {
        drawerPaperRef.current.style.transition = 'transform 0.3s ease-out';
        drawerPaperRef.current.style.transform = 'translateY(0)';
        setTimeout(() => {
          if (drawerPaperRef.current) {
            drawerPaperRef.current.style.transition = '';
          }
          setSwipePosition(0);
        }, 300);
      }
    },
    trackMouse: false,
    trackTouch: true,
    preventScrollOnSwipe: true,
    delta: 10,
  });

  // Uzyskaj pełną ścieżkę kategorii (breadcrumbs)
  const getCategoryPath = (category: Category | null): Category[] => {
    const path: Category[] = [];
    let currentCat = category;

    while (currentCat) {
      path.unshift(currentCat);
      currentCat = currentCat.parentCategory || null;
    }

    return path;
  };

  const categoryPath = getCategoryPath(currentCategory);

  // Filtruj podkategorie na podstawie wyszukiwania
  const filteredCategories = categories.filter(
    (category) =>
      !categorySearchQuery ||
      category.name.toLowerCase().includes(categorySearchQuery.toLowerCase()),
  );

  // Animacje
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0 },
  };

  // Zawartość dla widoku mobilnego
  const renderMobileContent = () => {
    return (
      <motion.div
        className="relative flex flex-col w-full h-full max-h-[85vh] overflow-hidden bg-white"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Handle for dragging on mobile devices */}
        <div className="flex justify-center py-2 cursor-grab active:cursor-grabbing touch-pan-y">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex-shrink-0 border-b border-gray-200 sticky top-0 z-10 bg-white">
          <div className="flex items-center justify-between p-4">
            <h2 className="text-lg font-medium text-gray-900">
              Filtrowanie i kategorie
            </h2>
            <motion.button
              whileHover={{ rotate: 90 }}
              transition={{ duration: 0.2 }}
              onClick={() => setIsSidePanelOpen(false)}
              className="rounded-full p-1 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              <Close />
            </motion.button>
          </div>

          {/* Tabs */}
          <div className="flex border-t border-gray-200">
            <motion.button
              className={`relative flex-1 py-3 text-center text-sm font-medium ${
                activeTab === 'categories'
                  ? 'text-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('categories')}
              whileHover={{ backgroundColor: 'rgba(0,0,0,0.03)' }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-center space-x-1">
                <CategoryOutlined fontSize="small" />
                <span>Kategorie</span>
              </div>
              {activeTab === 'categories' && (
                <motion.div
                  className="absolute bottom-0 left-0 right-0 h-0.5"
                  style={{ backgroundColor: colors.primary }}
                  layoutId="activeTabIndicator"
                />
              )}
            </motion.button>

            {producers && producers.length > 0 && (
              <motion.button
                className={`relative flex-1 py-3 text-center text-sm font-medium ${
                  activeTab === 'producers'
                    ? 'text-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('producers')}
                whileHover={{ backgroundColor: 'rgba(0,0,0,0.03)' }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center justify-center space-x-1">
                  <Business fontSize="small" />
                  <span>Producenci</span>
                  {selectedProducers.length > 0 && (
                    <span
                      className="ml-1 text-xs text-white px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: colors.primary }}
                    >
                      {selectedProducers.length}
                    </span>
                  )}
                </div>
                {activeTab === 'producers' && (
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-0.5"
                    style={{ backgroundColor: colors.primary }}
                    layoutId="activeTabIndicator"
                  />
                )}
              </motion.button>
            )}

            <motion.button
              className={`relative flex-1 py-3 text-center text-sm font-medium ${
                activeTab === 'filters'
                  ? 'text-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('filters')}
              whileHover={{ backgroundColor: 'rgba(0,0,0,0.03)' }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-center space-x-1">
                <FilterListOutlined fontSize="small" />
                <span>Filtry</span>
              </div>
              {activeTab === 'filters' && (
                <motion.div
                  className="absolute bottom-0 left-0 right-0 h-0.5"
                  style={{ backgroundColor: colors.primary }}
                  layoutId="activeTabIndicator"
                />
              )}
            </motion.button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-grow overflow-y-auto">
          <AnimatePresence mode="wait">
            {activeTab === 'categories' && (
              <motion.div
                key="categories"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="p-4 space-y-4"
              >
                {/* Ścieżka kategorii (breadcrumbs) */}
                {currentCategory && (
                  <motion.div
                    variants={itemVariants}
                    className="flex flex-wrap items-center text-sm mb-2 gap-1"
                  >
                    <button
                      onClick={() => {
                        const newSearchParams = new URLSearchParams(
                          searchParams,
                        );
                        newSearchParams.delete('categoryId');
                        setSearchParams(newSearchParams);
                      }}
                      className="hover:underline inline-flex items-center"
                      style={{ color: colors.primary }}
                    >
                      <HomeIcon className="w-4 h-4 mr-1" />
                      <span>Wszystkie</span>
                    </button>

                    {categoryPath.map((cat, index) => (
                      <React.Fragment key={cat.id}>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                        <button
                          onClick={() => {
                            const newSearchParams = new URLSearchParams(
                              searchParams,
                            );
                            newSearchParams.set(
                              'categoryId',
                              cat.id.toString(),
                            );
                            setSearchParams(newSearchParams);
                          }}
                          className={`${
                            index === categoryPath.length - 1
                              ? 'font-semibold text-gray-800'
                              : 'hover:underline'
                          }`}
                          style={
                            index !== categoryPath.length - 1
                              ? { color: colors.primary }
                              : {}
                          }
                        >
                          {cat.name}
                        </button>
                      </React.Fragment>
                    ))}
                  </motion.div>
                )}

                {!currentCategory && (
                  <motion.div variants={itemVariants} className="mb-2">
                    <div className="font-semibold text-gray-800 flex items-center">
                      <HomeIcon className="w-4 h-4 mr-1" />
                      <span>Wszystkie produkty</span>
                    </div>
                  </motion.div>
                )}

                {/* Aktualna kategoria */}
                {currentCategory && (
                  <motion.div variants={itemVariants}>
                    <div
                      className={`bg-${config.theme.mainColor}-50 p-3 rounded-md border border-${config.theme.mainColor}-100`}
                    >
                      <div className="flex items-start">
                        <div className="mr-3">
                          <div
                            className={`w-8 h-8 bg-${config.theme.mainColor}-100 rounded-full flex items-center justify-center text-${config.theme.mainColor}-600`}
                          >
                            <FolderOutlined fontSize="small" />
                          </div>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-800">
                            {currentCategory.name}
                          </h3>
                        </div>
                      </div>

                      {currentCategory.parentCategory && (
                        <button
                          className="flex items-center text-xs mt-2 hover:underline"
                          style={{ color: colors.primary }}
                          onClick={() => {
                            const newSearchParams = new URLSearchParams(
                              searchParams,
                            );
                            currentCategory?.parentCategory
                              ? newSearchParams.set(
                                  'categoryId',
                                  currentCategory.parentCategory.id.toString(),
                                )
                              : newSearchParams.delete('categoryId');
                            setSearchParams(newSearchParams);
                          }}
                        >
                          <ChevronLeft fontSize="small" />
                          <span>
                            Powrót do {currentCategory.parentCategory.name}
                          </span>
                        </button>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Wyszukiwanie podkategorii */}
                {categories.length > 5 && (
                  <motion.div variants={itemVariants}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Szukaj w kategoriach..."
                      variant="outlined"
                      value={categorySearchQuery}
                      onChange={(e) => setCategorySearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <SearchIcon
                            fontSize="small"
                            className="mr-2 text-gray-400"
                          />
                        ),
                        className: 'bg-gray-50 text-sm',
                      }}
                    />
                  </motion.div>
                )}

                {/* Lista podkategorii */}
                {filteredCategories.length > 0 ? (
                  <motion.div variants={itemVariants} className="space-y-1">
                    <h4 className="text-xs text-gray-500 uppercase font-medium mb-1 mt-2">
                      {currentCategory ? 'Podkategorie' : 'Główne kategorie'}:
                    </h4>
                    <List dense disablePadding className="space-y-1">
                      {filteredCategories.map((category) => (
                        <ListItemButton
                          key={category.id}
                          onClick={() => {
                            const newSearchParams = new URLSearchParams(
                              searchParams,
                            );
                            newSearchParams.set(
                              'categoryId',
                              category.id.toString(),
                            );
                            setSearchParams(newSearchParams);
                          }}
                          className={`rounded-md py-2 transition-colors hover:bg-${config.theme.mainColor}-50`}
                        >
                          <div className="flex items-center w-full">
                            <div className="w-6 h-6 bg-gray-100 rounded-md flex items-center justify-center mr-2 text-gray-600">
                              <FolderOutlined fontSize="small" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-gray-800 font-medium truncate">
                                {category.name}
                              </div>
                            </div>
                            {category.subCategories &&
                              category.subCategories.length > 0 && (
                                <div className="flex items-center gap-1">
                                  <div
                                    className={`px-1.5 py-0.5 bg-${config.theme.mainColor}-50 text-${config.theme.mainColor}-600 text-xs font-medium rounded-md`}
                                  >
                                    {category.subCategories.length}
                                  </div>
                                  <ChevronRight className="text-gray-400" />
                                </div>
                              )}
                          </div>
                        </ListItemButton>
                      ))}
                    </List>
                  </motion.div>
                ) : categorySearchQuery ? (
                  <motion.div
                    variants={itemVariants}
                    className="p-3 text-center bg-gray-50 rounded-md"
                  >
                    <p className="text-gray-500 text-sm">
                      Brak kategorii pasujących do "{categorySearchQuery}"
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    variants={itemVariants}
                    className="p-3 text-center bg-gray-50 rounded-md"
                  >
                    <p className="text-gray-500 text-sm">Brak podkategorii</p>
                  </motion.div>
                )}
              </motion.div>
            )}

            {activeTab === 'producers' && producers && producers.length > 0 && (
              <motion.div
                key="producers"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="p-4"
              >
                <Producers
                  producers={producers}
                  selectedProducers={selectedProducers} // używamy propsa, nie lokalnego stanu
                  onProducerChange={onProducerChange} // używamy funkcji z propsów
                  onClearAllProducers={onClearAllProducers} // Przekazanie nowego propsa
                  isMobile={true} // Dodaj ten parametr
                />
              </motion.div>
            )}

            {activeTab === 'filters' && (
              <motion.div
                key="filters"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="p-4"
              >
                {/* Komponent Filters z nowymi propsami */}
                <Filters
                  parameters={parameters}
                  selectedFilters={selectedFilters}
                  onFilterChange={onFilterChange}
                  onClearAllFilters={onClearAllFilters}
                  onApplyFilters={onApplyFilters}
                  minPrice={minPrice}
                  maxPrice={maxPrice}
                  totalPriceRange={totalPriceRange}
                  onPriceRangeChange={onPriceRangeChange}
                  isDesktop={false}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer z informacją o automatycznym zastosowaniu zmian */}
        <div className="flex-shrink-0 border-t border-gray-200 p-4">
          <motion.button
            onClick={() => setIsSidePanelOpen(false)}
            className="mt-3 w-full flex items-center justify-center py-2.5 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            whileHover={{ y: -1, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
            whileTap={{ y: 0 }}
          >
            Zamknij panel
          </motion.button>
        </div>
      </motion.div>
    );
  };

  // Zawartość dla widoku desktopowego
  const renderDesktopContent = () => {
    return (
      <div className="w-full h-full overflow-y-auto space-y-5">
        {priceRange && currentPrice && (
          <>
            {/* Sekcja kategorii */}
            <motion.div
              className="rounded-lg border shadow-sm overflow-hidden"
              style={{
                background: `linear-gradient(to bottom right, ${primaryLight}, white)`,
                borderColor: primaryLightBorder,
              }}
              initial="hidden"
              animate="visible"
              variants={containerVariants}
            >
              <div style={{ borderBottom: `1px solid ${primaryLightBorder}` }}>
                <ListItemButton
                  onClick={() => setOpenCategorySection(!openCategorySection)}
                  className="py-3"
                >
                  <motion.div
                    variants={itemVariants}
                    className="flex items-center mr-2"
                    style={{ color: colors.primary }}
                  >
                    <CategoryOutlined fontSize="small" />
                  </motion.div>
                  <ListItemText
                    primary={
                      <motion.span
                        variants={itemVariants}
                        className="font-bold text-gray-800"
                      >
                        Kategorie produktów
                      </motion.span>
                    }
                  />
                  <KeyboardArrowDown
                    className={`transition-transform duration-300 ${openCategorySection ? 'rotate-180' : ''}`}
                  />
                </ListItemButton>
              </div>

              <Collapse in={openCategorySection}>
                <div className="p-3 space-y-3">
                  {/* Zawartość sekcji kategorii pozostaje taka sama dla widoku desktopowego */}
                  {/* Ścieżka kategorii (breadcrumbs) */}
                  {currentCategory && (
                    <motion.div
                      variants={itemVariants}
                      className="flex flex-wrap items-center text-sm mb-2 gap-1"
                    >
                      <button
                        onClick={() => {
                          const newSearchParams = new URLSearchParams(
                            searchParams,
                          );
                          newSearchParams.delete('categoryId');
                          setSearchParams(newSearchParams);
                        }}
                        className="hover:underline inline-flex items-center"
                        style={{ color: colors.primary }}
                      >
                        <HomeIcon className="w-4 h-4 mr-1" />
                        <span>Wszystkie</span>
                      </button>

                      {categoryPath.map((cat, index) => (
                        <React.Fragment key={cat.id}>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                          <button
                            onClick={() => {
                              const newSearchParams = new URLSearchParams(
                                searchParams,
                              );
                              newSearchParams.set(
                                'categoryId',
                                cat.id.toString(),
                              );
                              setSearchParams(newSearchParams);
                            }}
                            className={`${index === categoryPath.length - 1 ? 'font-semibold text-gray-800' : 'hover:underline'}`}
                            style={
                              index !== categoryPath.length - 1
                                ? { color: colors.primary }
                                : {}
                            }
                          >
                            {cat.name}
                          </button>
                        </React.Fragment>
                      ))}
                    </motion.div>
                  )}

                  {!currentCategory && (
                    <motion.div variants={itemVariants} className="mb-2">
                      <div className="font-semibold text-gray-800 flex items-center">
                        <HomeIcon className="w-4 h-4 mr-1" />
                        <span>Wszystkie produkty</span>
                      </div>
                    </motion.div>
                  )}

                  {/* Aktualna kategoria */}
                  {currentCategory && (
                    <motion.div variants={itemVariants}>
                      <div
                        className={`bg-${config.theme.mainColor}-50 p-3 rounded-md border border-${config.theme.mainColor}-100`}
                      >
                        <div className="flex items-start">
                          <div className="mr-3">
                            <div
                              className={`w-8 h-8 bg-${config.theme.mainColor}-100 rounded-full flex items-center justify-center text-${config.theme.mainColor}-600`}
                            >
                              <FolderOutlined fontSize="small" />
                            </div>
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-800">
                              {currentCategory.name}
                            </h3>
                          </div>
                        </div>

                        {currentCategory.parentCategory && (
                          <button
                            className="flex items-center text-xs mt-2 hover:underline"
                            style={{ color: colors.primary }}
                            onClick={() => {
                              const newSearchParams = new URLSearchParams(
                                searchParams,
                              );
                              currentCategory?.parentCategory
                                ? newSearchParams.set(
                                    'categoryId',
                                    currentCategory.parentCategory.id.toString(),
                                  )
                                : newSearchParams.delete('categoryId');
                              setSearchParams(newSearchParams);
                            }}
                          >
                            <ChevronLeft fontSize="small" />
                            <span>
                              Powrót do {currentCategory.parentCategory.name}
                            </span>
                          </button>
                        )}
                      </div>
                    </motion.div>
                  )}

                  {/* Wyszukiwanie podkategorii */}
                  {categories.length > 5 && (
                    <motion.div variants={itemVariants}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Szukaj w kategoriach..."
                        variant="outlined"
                        value={categorySearchQuery}
                        onChange={(e) => setCategorySearchQuery(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <SearchIcon
                              fontSize="small"
                              className="mr-2 text-gray-400"
                            />
                          ),
                          className: 'bg-gray-50 text-sm',
                        }}
                      />
                    </motion.div>
                  )}

                  {/* Lista podkategorii */}
                  {filteredCategories.length > 0 ? (
                    <motion.div variants={itemVariants} className="space-y-1">
                      <h4 className="text-xs text-gray-500 uppercase font-medium mb-1 mt-2">
                        {currentCategory ? 'Podkategorie' : 'Główne kategorie'}:
                      </h4>
                      <List dense disablePadding className="space-y-1">
                        {filteredCategories.map((category) => (
                          <ListItemButton
                            key={category.id}
                            onClick={() => {
                              const newSearchParams = new URLSearchParams(
                                searchParams,
                              );
                              newSearchParams.set(
                                'categoryId',
                                category.id.toString(),
                              );
                              setSearchParams(newSearchParams);
                            }}
                            className={`rounded-md py-2 transition-colors hover:bg-${config.theme.mainColor}-50`}
                          >
                            <div className="flex items-center w-full">
                              <div className="w-6 h-6 bg-gray-100 rounded-md flex items-center justify-center mr-2 text-gray-600">
                                <FolderOutlined fontSize="small" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-gray-800 font-medium truncate">
                                  {category.name}
                                </div>
                              </div>
                              {category.subCategories &&
                                category.subCategories.length > 0 && (
                                  <div className="flex items-center gap-1">
                                    <div className={`px-1.5 py-0.5 bg-${config.theme.mainColor}-50 text-${config.theme.mainColor}-600 text-xs font-medium rounded-md`}>
                                      {category.subCategories.length}
                                    </div>
                                    <ChevronRight className="text-gray-400" />
                                  </div>
                                )}
                            </div>
                          </ListItemButton>
                        ))}
                      </List>
                    </motion.div>
                  ) : categorySearchQuery ? (
                    <motion.div
                      variants={itemVariants}
                      className="p-3 text-center bg-gray-50 rounded-md"
                    >
                      <p className="text-gray-500 text-sm">
                        Brak kategorii pasujących do "{categorySearchQuery}"
                      </p>
                    </motion.div>
                  ) : (
                    <motion.div
                      variants={itemVariants}
                      className="p-3 text-center bg-gray-50 rounded-md"
                    >
                      <p className="text-gray-500 text-sm">Brak podkategorii</p>
                    </motion.div>
                  )}
                </div>
              </Collapse>
            </motion.div>

            {/* Komponent Producers */}
            {producers && producers.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="mb-5"
              >
                <Producers
                  producers={producers}
                  selectedProducers={selectedProducers} // używamy propsa, nie lokalnego stanu
                  onProducerChange={onProducerChange} // używamy funkcji z propsów
                  onClearAllProducers={onClearAllProducers} // Przekazanie nowego propsa
                  isMobile={false} // Dodaj ten parametr (można pominąć, ponieważ domyślnie jest false)
                />
              </motion.div>
            )}

            {/* Sekcja filtrów */}

            {/* Komponent Filters z nowymi propsami */}
            <motion.div
              variants={itemVariants}
              className="rounded-lg border shadow-sm overflow-hidden"
              style={{
                background: `linear-gradient(to bottom right, ${primaryLight}, white)`,
                borderColor: primaryLightBorder,
              }}
            >
              <div style={{ borderBottom: `1px solid ${primaryLightBorder}` }}>
                <ListItemButton
                  onClick={() => setOpenFiltersSection(!openFiltersSection)}
                  className="py-3"
                >
                  <div
                    className="flex items-center mr-2"
                    style={{ color: colors.primary }}
                  >
                    <FilterAlt fontSize="small" />
                  </div>
                  <ListItemText
                    primary={
                      <div className="flex items-center">
                        <span className="font-bold text-gray-800">Filtry</span>
                      </div>
                    }
                  />
                  <KeyboardArrowDown
                    className={`transition-transform duration-300 ${openFiltersSection ? 'rotate-180' : ''}`}
                  />
                </ListItemButton>
              </div>

              <Collapse in={openFiltersSection}>
                <Filters
                  parameters={parameters}
                  selectedFilters={selectedFilters}
                  onFilterChange={onFilterChange}
                  onClearAllFilters={onClearAllFilters}
                  onApplyFilters={onApplyFilters}
                  minPrice={minPrice}
                  maxPrice={maxPrice}
                  totalPriceRange={totalPriceRange}
                  onPriceRangeChange={onPriceRangeChange}
                  isDesktop={true}
                />
              </Collapse>
            </motion.div>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={isSidePanelOpen}
          onClose={() => setIsSidePanelOpen(false)}
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: '100%',
              height: 'calc(100% - 60px)',
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
              overflowY: 'hidden',
              touchAction: 'pan-x', // Allow only horizontal swiping
              willChange: 'transform',
            },
            ref: (el: any) => {
              drawerPaperRef.current = el;
              if (isMobile && el) {
                handlers.ref(el);
              }
            },
          }}
          transitionDuration={{
            enter: 350,
            exit: 300,
          }}
          SlideProps={{
            direction: 'up',
          }}
          ModalProps={{
            keepMounted: true,
          }}
        >
          {renderMobileContent()}
        </Drawer>
      ) : (
        renderDesktopContent()
      )}
    </>
  );
}

// Ikona Home - dodana jako komponent lokalny
const HomeIcon = ({ className = '' }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 19l9 2-9-9-9 9 2-9 9-2z"
    />
  </svg>
);

export default SearchSidePanel;
